import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Circle, Link as LinkIcon, Copy } from "lucide-react";
import { toast } from "sonner";
import { adminAuthHeader } from "@/hooks/useAdminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const KIND_LABEL = {
  unlock_bundle_234:  "Sblocco L2+L3+L4 gratis",
  unlock_full_course: "Sblocco Corso Completo gratis",
};

/**
 * Shows the magic-link tracking history for a user: every generated link with
 * status (generated → clicked → action_taken). Used by the admin to measure
 * conversion of the magic-link funnel.
 */
export default function TrackedLinksPanel({ userId, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/admin/users/${userId}/tracked-links`, { headers: adminAuthHeader() })
      .then((r) => { if (alive) setItems(r.data?.items || []); })
      .catch(() => { /* ignore */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userId, refreshKey]);

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copiato"); }
    catch { window.prompt("Copia manualmente:", url); }
  };

  return (
    <div
      className="rounded-2xl p-5 border"
      style={{ borderColor: "#dbe3ec", background: "#fff" }}
      data-testid="tracked-links-panel"
    >
      <div className="flex items-center gap-2 mb-4">
        <LinkIcon size={16} style={{ color: "var(--np-secondary)" }} />
        <h3 className="text-[13px] font-semibold tracking-[0.16em] uppercase" style={{ color: "var(--np-primary)" }}>
          Link generati ({items.length})
        </h3>
      </div>
      {loading ? (
        <p className="text-sm italic" style={{ color: "var(--np-muted)" }}>Caricamento…</p>
      ) : items.length === 0 ? (
        <p className="text-sm italic" style={{ color: "var(--np-muted)" }}>
          Nessun link generato. Usa i bottoni &quot;🎁 Genera link…&quot; sopra per inviare uno sblocco gratuito.
        </p>
      ) : (
        <ul className="divide-y" style={{ borderColor: "#eef1f5" }}>
          {items.map((tl) => {
            const isClicked = !!tl.clicked_at;
            const isClaimed = !!tl.action_taken_at;
            return (
              <li key={tl.id} className="py-3 grid gap-1" data-testid={`tracked-link-row-${tl.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-sm font-semibold" style={{ color: "var(--np-primary)" }}>
                    {KIND_LABEL[tl.kind] || tl.kind}
                  </p>
                  <div className="flex items-center gap-3 text-[11px]">
                    <StatusDot ok done label="Generato" date={formatDate(tl.created_at)} />
                    <StatusDot ok={isClicked} done={isClicked} label="Cliccato" date={isClicked ? formatDate(tl.clicked_at) : "Non ancora"} />
                    <StatusDot ok={isClaimed} done={isClaimed} label="Azione" date={isClaimed ? formatDate(tl.action_taken_at) : "Non ancora"} />
                    <button
                      type="button"
                      onClick={() => copyLink(tl.full_url)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px] font-semibold"
                      style={{ background: "#f1f5f9", borderColor: "#cbd5e1", color: "#334155" }}
                      title="Copia link negli appunti"
                      data-testid={`tracked-link-copy-${tl.id}`}
                    >
                      <Copy size={11} /> Copia
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function StatusDot({ ok, done, label, date }) {
  const Icon = done ? CheckCircle2 : Circle;
  const color = done ? "#16a34a" : (ok ? "#16a34a" : "#94a3b8");
  return (
    <div className="inline-flex items-center gap-1.5">
      <Icon size={13} style={{ color }} />
      <span style={{ color: done ? "#0f5132" : "#64748b" }}>
        <strong>{label}</strong>{date ? ` · ${date}` : ""}
      </span>
    </div>
  );
}
